import {
  FieldMessage,
  Stack,
  Tag,
  type Autosuggest,
} from 'braid-design-system';
import { useState } from 'react';

import {
  EMPTY_SUGGESTION,
  HirerAutosuggest,
  type HirerAutosuggestValue,
} from '#src/components/HirerAutosuggest/HirerAutosuggest.tsx';
import type { HiringOrganizationIdFieldsFragment } from '#src/types/generated/graphql.ts';

export const useHirerState = () => {
  const [hirer, setHirer] = useState<HiringOrganizationIdFieldsFragment>();

  const [suggestion, setSuggestion] =
    useState<HirerAutosuggestValue>(EMPTY_SUGGESTION);

  return {
    hirer,
    setHirer,
    setSuggestion,
    suggestion,
  };
};

interface Props extends ReturnType<typeof useHirerState> {
  id: string;
  disabled?: boolean;
  extraDescription?: string | null;
  extraDescriptionTone?: React.ComponentProps<typeof Autosuggest>['tone'];
}

export const HirerPicker = ({
  disabled,
  hirer,
  id,
  setHirer,
  setSuggestion,
  suggestion,
  extraDescription,
  extraDescriptionTone,
}: Props) =>
  hirer ? (
    <Tag clearLabel="Remove" onClear={() => setHirer(undefined)}>
      {`${hirer.name} (${hirer.seekAnzAdvertiserId})`}
    </Tag>
  ) : (
    <Stack space="small">
      <HirerAutosuggest
        id={id}
        onChange={(selection) => {
          const selectedHirer = selection.value;

          if (selectedHirer) {
            setHirer(selectedHirer);

            setSuggestion(EMPTY_SUGGESTION);
          } else {
            setSuggestion(selection);
          }
        }}
        onClear={() => setSuggestion(EMPTY_SUGGESTION)}
        value={suggestion}
        description="You can use this field to scope the subscription to only send events that relate to a specific hirer. Leave it blank to receive events from all hirers."
        disabled={disabled}
        label="Hirer ID"
        secondaryLabel="Optional"
      />

      {extraDescription ? (
        <FieldMessage
          id={`${id}-message`}
          message={extraDescription}
          tone={extraDescriptionTone}
        />
      ) : null}
    </Stack>
  );
